import { Injectable } from '@angular/core';
import { ConnectionService } from '@services/connection-service';
import { EventLoggerService } from '@services/event-logger-service';
import { ExperimentService } from '@services/experiment';
import { InstantCheckupService } from '@services/instant-checkup-service';
import { FallbackSummaryMap, FallbackSummaries } from 'e2e/src/shared/constants';
import { problemsCopy, concernSeverityTranslations } from '../../app/instant-checkup/problem-details';
import { concernsTranslation } from '../../static-translations/concern-translations';
import { AppConfig } from '../../app/app.config';
import { extractRegimenProducts, extractHeroIngredients } from 'client/helper/regimen-helper';
import { ApiClientConstant } from '@cureskin/api-client';
import {
  InstantCheckup, RegimenVariant, HeroIngredient, User,
} from '@cureskin/api-client/src/server-api';
import {
  ConcernsWithCount, SkinData, HairData, SkinParameters, ConcernCountResult, HeroIngredientsResult,
  PRIVATE_MAIN_CONCERN_DISPLAY_NAME, HAIR_CONCERNS, CONCERN_NAMES,
  PROBLEM_TO_CONCERN, FIRST_JOURNEY_CONCERNS, HAIR_REPORT_FALLBACK_PARAMS, SKIN_REPORT_FALLBACK_PARAMS, DetectionResult, SkinReportInfo,
  AiHairReport, AiLocalizedString, AiLocalizedSummary, ReportDetails,
} from '../../app/report/report.constants';
import {
  AiParameterInput, HairParametersInfo, SkinParametersInfo,
  buildAiParameters, buildUserHairParameters, buildUserSkinParameters,
} from '../../helper/report-helper/report-helper';
import { Regimen } from "../../app/app-constant-types";
import { REGIMEN_CLASS_TO_INSTANT_CHECKUP_TYPES } from '../../app/app.constants';


@Injectable()
export class ReportService {
  static readonly MINIMUM_REQUIRED_HERO_INGREDIENTS = 3;
  static readonly MAXIMUM_REQUIRED_HERO_INGREDIENTS = 6;
  static readonly MINIMUM_REQUIRED_HERO_INGREDIENTS_EXISTING_UI = 4;

  constructor(
    private readonly conn: ConnectionService,
    private readonly appConfig: AppConfig,
    private readonly instantCheckupService: InstantCheckupService,
    private readonly eventLogger: EventLoggerService,
    private readonly experimentService: ExperimentService,
  ) {}

  private aiDetectionCheckups: InstantCheckup[] = [];
  private consultationSessionMainConcern: string | undefined;
  private aiDetectionExcludeList: string[] = [];

  private mostRecentPerType(checkups: InstantCheckup[], types: string[]): InstantCheckup[] {
    const byType = new Map<string, InstantCheckup>();
    for (const checkup of checkups ?? []) {
      const type = checkup?.type;
      if (type && types.includes(type) && !byType.has(type)) byType.set(type, checkup);
    }
    // Emit in the canonical `types` order (FRONT/LEFT/RIGHT face, HAIR_FRONT/HAIR_TOP),
    // not the order the checkups happened to arrive in.
    return types
      .map((type: string): InstantCheckup | undefined => byType.get(type))
      .filter((checkup: InstantCheckup | undefined): checkup is InstantCheckup => !!checkup);
  }

  isAiDetectionEnabledForClass(regimenClass: string): boolean {
    const normalizedClass = (regimenClass || '').toUpperCase();
    return this.experimentService.getReportAiDetectionClasses()
      .map((c): string => c.toUpperCase())
      .includes(normalizedClass);
  }

  getCheckupsForAiDetections(checkups: InstantCheckup[], regimenClass: string): InstantCheckup[] {
    if (!this.isAiDetectionEnabledForClass(regimenClass)) return [];
    const normalizedClass = (regimenClass || '').toUpperCase();
    const typeMap = REGIMEN_CLASS_TO_INSTANT_CHECKUP_TYPES as Record<string, ReadonlyArray<string>>;
    const types = typeMap[normalizedClass] ?? [];
    return this.mostRecentPerType(checkups ?? [], [...types]);
  }

  async buildAiDetectionContext(rawCheckups: InstantCheckup[], regimenClass: string): Promise<void> {
    this.aiDetectionCheckups = this.getCheckupsForAiDetections(rawCheckups, regimenClass);
    if (!this.aiDetectionCheckups.length) return;
    this.aiDetectionExcludeList = this.experimentService.getExcludeInstantCheckupProblems();
    // consultationSessionMainConcern only feeds the FACE renderer; skip the fetch for other classes.
    if ((regimenClass || '').toUpperCase() === this.appConfig.Shared.Regimen.Class.FACE) {
      const session = await this.conn.findConsultationSession(this.appConfig.Shared.Regimen.Class.FACE);
      this.consultationSessionMainConcern = session?.get('PrivateMainConcern');
    }
  }

  getAiDetectionCheckups(): InstantCheckup[] {
    return this.aiDetectionCheckups;
  }

  showAiDetectionCheckups(): boolean {
    return this.aiDetectionCheckups.length > 0;
  }

  getConsultationSessionMainConcern(): string | undefined {
    return this.consultationSessionMainConcern;
  }

  getAiDetectionExcludeList(): string[] {
    return this.aiDetectionExcludeList;
  }

  filterCheckups(checkups: InstantCheckup[], isHair: boolean): InstantCheckup[] {
    if (!checkups?.length) return [];

    const filtered: InstantCheckup[] = [];
    if (isHair) {
      const hairFront = checkups?.find((c: InstantCheckup): boolean => c?.type?.includes(ApiClientConstant.InstantCheckup.Type.HAIR_FRONT));
      const hairTop = checkups?.find((c: InstantCheckup): boolean => c?.type?.includes(ApiClientConstant.InstantCheckup.Type.HAIR_TOP));
      const hairLegacy = checkups?.find((c: InstantCheckup): boolean => c?.type === ApiClientConstant.InstantCheckup.Type.HAIR);

      if (hairFront) filtered.push(hairFront);
      else if (hairLegacy) filtered.push(hairLegacy);
      if (hairTop) filtered.push(hairTop);
    } else {
      const frontFace = checkups?.find((c: InstantCheckup): boolean => c?.type?.includes(ApiClientConstant.InstantCheckup.Type.FRONT_FACE));
      const sideFace = checkups?.find((c: InstantCheckup): boolean => c?.type?.includes(ApiClientConstant.InstantCheckup.Type.SIDE_FACE));

      if (frontFace) filtered.push(frontFace);
      if (sideFace) filtered.push(sideFace);
    }
    return filtered;
  }

  mergeDetectedConcerns(checkups: InstantCheckup[]): { mergedMap: Map<string, DetectionResult>, mergedConcerns: DetectionResult[] } {
    const mergedMap = new Map<string, DetectionResult>();
    const mergedConcerns: DetectionResult[] = [];

    if (!checkups?.length) return { mergedMap, mergedConcerns };

    const frontFaceDetection = checkups.find(
      (item: InstantCheckup): boolean => item.type === this.appConfig.Shared.InstantCheckup.Type.FRONT_FACE);
    const sideFaceDetection = checkups.find(
      (item: InstantCheckup): boolean => item.type !== this.appConfig.Shared.InstantCheckup.Type.FRONT_FACE);

    const allObjects: DetectionResult[] = [
      ...(frontFaceDetection?.aiResponse?.result || []),
      ...(sideFaceDetection?.aiResponse?.result || []),
    ];

    allObjects.forEach((checkup: DetectionResult): void => {
      const name = checkup?.ProblemName || problemsCopy.en[checkup.class]?.name;
      const existing = mergedMap.get(name);
      if (!existing || (checkup.BoundingBoxes?.length || 0) > (existing.BoundingBoxes?.length || 0)) {
        mergedMap.set(name, checkup);
      }
    });

    mergedMap.forEach((item: DetectionResult): void => {
      mergedConcerns.push(item);
    });

    return { mergedMap, mergedConcerns };
  }

  buildConcernCounts(mergedMap: Map<string, DetectionResult>, regimenConcernName: string): ConcernCountResult {
    const payload: ConcernsWithCount = {
      infAcne: 0, comedone: 0, openPores: 0, ds: 0, dc: 0, melasma: 0, acneScars: 0, sensitiveSkin: 0,
    };
    let mainConcernKey = 'infAcne';
    let mainConcern = '';

    mergedMap.forEach((obj: DetectionResult): void => {
      const mapping = PROBLEM_TO_CONCERN[obj?.ProblemName];
      if (!mapping) return;
      mainConcernKey = mapping.key;
      if (obj?.Condition === 'Detected') {
        payload[mapping.key] = mapping.useBoundingBoxCount ? (obj?.BoundingBoxes?.length ?? 0) : 1;
        if (!regimenConcernName && !mainConcern) {
          mainConcern = CONCERN_NAMES[mapping.key] || '';
        }
      }
    });

    return { payload, mainConcernKey, mainConcern };
  }

  parseSkinReportParams(regimen: Regimen, skinReport: SkinReportInfo): SkinData {
    const skinReportParamsFromRegimen = regimen?.skinReportParams || {};
    const { skinParameters } = skinReportParamsFromRegimen;
    const skinReportFallback = skinReport?.skinParameters;

    const parsed = this.parseStringifiedParams<SkinData>(skinParameters);

    if (Object.keys(parsed).length > 0) {
      return parsed;
    }
    if (skinReportFallback) return skinReportFallback;
    return this.getSkinReportFallbackParams(regimen);
  }

  private getSkinReportFallbackParams(regimen: Regimen): SkinData {
    const treatmentConcerns: string[] = regimen?.displayConcern?.treatmentConcerns ?? [];
    const primary = treatmentConcerns[0] ?? '';
    const secondary = treatmentConcerns[1] ?? '';

    const primaryFallback = SKIN_REPORT_FALLBACK_PARAMS[primary];
    if (!primaryFallback) return {} as SkinData;

    const rawSeverity = regimen?.displayConcern?.severity || '';
    const severity = rawSeverity
      ? rawSeverity.charAt(0).toUpperCase() + rawSeverity.slice(1).toLowerCase()
      : '';

    const entry = (secondary && primaryFallback[secondary])
      || (severity && primaryFallback[severity])
      || primaryFallback['default'];
    return (entry ?? {}) as SkinData;
  }

  parseHairReportParams(regimen: Regimen): HairData {
    const hairParameters = regimen?.hairReportParams?.hairParameters;
    if (hairParameters) {
      return this.parseStringifiedParams<HairData>(hairParameters);
    }
    return this.getHairReportFallbackParams(regimen);
  }

  private getHairReportFallbackParams(regimen: Regimen): HairData {
    const concernKey: string = regimen?.displayConcern?.treatmentConcerns?.[0] ?? '';
    const concernFallback = HAIR_REPORT_FALLBACK_PARAMS[concernKey];
    if (!concernFallback) return {} as HairData;

    if (concernKey === 'HAIR_LOSS') {
      const rawSeverity: string = regimen?.displayConcern?.severity || '';
      const severity = rawSeverity
        ? rawSeverity.charAt(0).toUpperCase() + rawSeverity.slice(1).toLowerCase()
        : 'Moderate';
      return (concernFallback[severity] ?? concernFallback['Moderate'] ?? {}) as HairData;
    }

    return (concernFallback['Moderate'] ?? {}) as HairData;
  }

  private parseStringifiedParams<T extends SkinData | HairData>(params: Record<string, string | SkinParameters>): T {
    const parsed: Record<string, string | SkinParameters> = { ...params };
    Object.keys(parsed).forEach((key: string): void => {
      if (typeof parsed[key] === 'string') {
        if ((parsed[key] as string).trim() === '') {
          parsed[key] = { patientScore: '', expectedRange: '' } as unknown as SkinParameters;
        } else {
          try {
            const formattedString = (parsed[key] as string)
              .replace(/\b(\w+)\b:/g, '"$1":')
              .replace(/\b(\d{1,4})-(\d{1,4})\b/g, '"$1-$2"');
            parsed[key] = JSON.parse(formattedString);
          } catch {
            parsed[key] = { patientScore: '', expectedRange: '' } as unknown as SkinParameters;
          }
        }
      }
    });
    return parsed as unknown as T;
  }

  async fetchAiHairReport(isHair: boolean): Promise<AiHairReport | null> {
    if (!isHair) return null;
    try {
      return await this.conn.getHairReport();
    } catch {
      return null;
    }
  }

  // Priority chain per field: see client/app/report/CLAUDE.md.
  buildReportDetails(
    aiReport: AiHairReport | null,
    fallback: {
      summary: string,
      primaryConcern: string,
      hasRegimenConcern: boolean,
      secondaryConcerns: string,
      severity: string,
      parameters: SkinData | HairData,
    },
    isHair: boolean,
    userLanguage: string = ApiClientConstant.LanguageString.EN,
  ): ReportDetails {
    const aiParameters = buildAiParameters((aiReport?.key_scalp_parameters ?? []).map((parameter): AiParameterInput => ({
      name: this.resolveAiLocalizedString(parameter.name, userLanguage),
      notScored: parameter.not_scored,
      targetRange: parameter.target_range,
      valuePercent: parameter.value_percent,
    })));
    const regimenParameters = isHair
      ? buildUserHairParameters(fallback.parameters as unknown as HairParametersInfo)
      : buildUserSkinParameters(fallback.parameters as unknown as SkinParametersInfo);

    return {
      summaryParagraphs: this.resolveAiSummaryParagraphs(aiReport, userLanguage)
        ?? [fallback.summary].filter((paragraph): boolean => !!paragraph?.trim()),
      primaryConcern: fallback.hasRegimenConcern
        ? fallback.primaryConcern
        : (this.resolveAiLocalizedString(aiReport?.main_concern, userLanguage) || fallback.primaryConcern),
      secondaryConcerns: fallback.secondaryConcerns || this.resolveAiSecondaryConcerns(aiReport, userLanguage),
      severity: fallback.severity,
      parameters: aiParameters.length ? aiParameters : regimenParameters,
    };
  }

  private resolveAiSecondaryConcerns(aiReport: AiHairReport | null, userLanguage: string): string {
    return (aiReport?.other_concerns ?? [])
      .map((concern): string => this.resolveAiLocalizedString(concern, userLanguage).trim())
      .filter((concern): boolean => !!concern)
      .join(', ');
  }

  private resolveAiSummaryParagraphs(aiReport: AiHairReport | null, userLanguage: string): string[] | null {
    const summary = this.resolveAiLocalizedSummary(aiReport?.overall_summary, userLanguage);
    const paragraphs = [summary?.paragraph_1, summary?.paragraph_2]
      .filter((paragraph): paragraph is string => !!paragraph?.trim());
    return paragraphs.length ? paragraphs : null;
  }

  private resolveAiLocalizedSummary(
    summary: AiHairReport['overall_summary'],
    userLanguage: string,
  ): AiLocalizedSummary | undefined {
    if (!summary) return undefined;
    if ('paragraph_1' in summary || 'paragraph_2' in summary) return summary as AiLocalizedSummary;

    const lang = this.getAiSupportedLanguage(userLanguage);
    return summary[lang] || summary[ApiClientConstant.LanguageString.EN];
  }

  private resolveAiLocalizedString(
    value: string | AiLocalizedString | null | undefined,
    userLanguage: string,
  ): string {
    if (!value) return '';
    if (typeof value === 'string') return value;

    const lang = this.getAiSupportedLanguage(userLanguage);
    return value[lang] || value[ApiClientConstant.LanguageString.EN] || '';
  }

  private getAiSupportedLanguage(userLanguage: string): 'en' | 'hi' {
    return userLanguage === ApiClientConstant.LanguageString.HI
      ? ApiClientConstant.LanguageString.HI
      : ApiClientConstant.LanguageString.EN;
  }


  async resolveOverallSummary(
    regimen: Regimen, isHair: boolean, mainConcern: string, concernsWithCount: ConcernsWithCount,
    userLanguage: string = ApiClientConstant.LanguageString.EN,
  ): Promise<string> {
    const overAllSummary = await this.fetchOverallSummary(regimen);
    if (!isHair) this.logSkinReportParams(concernsWithCount);
    if (!overAllSummary) return this.assignFallbackSummary(mainConcern, userLanguage, isHair);
    return overAllSummary;
  }

  private async fetchOverallSummary(regimen: Regimen): Promise<string | null> {
    const pointer = regimen?.overallSummary;
    const isTranslatedStringPresent = regimen?.overallSummaryTranslated?.length;
    if (pointer?.objectId && !isTranslatedStringPresent) {
      const [languageString] = await this.conn.getLanguageStringsById([pointer.objectId]);
      if (languageString) {
        return this.conn.getValueOfLanguageString(languageString);
      }
    }
    return isTranslatedStringPresent ? regimen?.overallSummaryTranslated : null;
  }

  private assignFallbackSummary(
    mainConcern: string,
    userLanguage: string = ApiClientConstant.LanguageString.EN,
    isHair: boolean = false,
  ): string {
    const lang = FallbackSummaryMap[userLanguage] ? userLanguage : ApiClientConstant.LanguageString.EN;
    const langMap = FallbackSummaryMap[lang];
    const langSummaries = FallbackSummaries[lang] || FallbackSummaries.en;

    const matchingKey = Object.keys(langMap).find(
      (key: string): boolean => key === mainConcern
    );

    const defaultSummary = isHair ? langSummaries.GENERAL_HAIR : langSummaries.GENERAL;
    return matchingKey ? langMap[matchingKey] : defaultSummary;
  }

  resolvePrimaryConcern(regimen: Regimen, mainConcern: string, detectedConcerns: string[], isHair: boolean, userLanguage: string = ApiClientConstant.LanguageString.EN): string {
    if (isHair) {
      // Prefer displayConcern.name; fall back to the legacy treatmentConcerns mapping when it's absent.
      if (regimen?.displayConcern?.name) return regimen.displayConcern.name;
      const treatmentConcerns: string[] = regimen?.displayConcern?.treatmentConcerns ?? [];
      const lang = PRIVATE_MAIN_CONCERN_DISPLAY_NAME[userLanguage] ? userLanguage : ApiClientConstant.LanguageString.EN;
      return PRIVATE_MAIN_CONCERN_DISPLAY_NAME[lang]?.[treatmentConcerns[0]] ?? PRIVATE_MAIN_CONCERN_DISPLAY_NAME[lang]['REGULAR_HAIR_CARE'] ?? '';
    }
    const finalConcern =  regimen?.displayConcern?.name || mainConcern || this.getFirstValidDetectedConcern(detectedConcerns);
    return this.translateDetectedConcern(finalConcern, userLanguage)
  }

  getFirstValidDetectedConcern(detectedConcerns: string[]): string {
    return detectedConcerns?.find(
      (concern: string) => !HAIR_CONCERNS.some(hc => concern.toLowerCase().includes(hc))
    ) ?? '';
  }

  resolveOtherConcerns(
    regimen: Regimen, mainConcern: string, detectedConcerns: string[],
    firstFrontFaceCheckup: InstantCheckup | undefined, isHair: boolean, userLanguage: string = ApiClientConstant.LanguageString.EN,
  ): string {
    if (isHair) return this.resolveHairOtherConcerns(detectedConcerns, userLanguage);

    const concernString = this.getConcernName(mainConcern);
    const melasmaSeverity = this.getMelasmaSeverity(firstFrontFaceCheckup);
    const melasmaConcern = this.appConfig.Shared.SkinReport.Concern.MELASMA;
    const isMildMelasma = melasmaSeverity === this.appConfig.Shared.InstantCheckup.Severity.MILD;
    const treatmentConcerns: string[] = regimen?.displayConcern?.treatmentConcerns ?? [];
    const lang = PRIVATE_MAIN_CONCERN_DISPLAY_NAME[userLanguage] ? userLanguage : ApiClientConstant.LanguageString.EN;

    const treatmentConcernDisplayNames = treatmentConcerns
      .map((tc: string): string => PRIVATE_MAIN_CONCERN_DISPLAY_NAME[lang]?.[tc] ?? tc)
      .filter((name: string): boolean => !!name);

    return detectedConcerns
      .filter((concern: string): boolean => {
        const isMainConcern = concern.toLowerCase().trim() === concernString.toLowerCase().trim();
        const isMelasmaButNotMild = concern === melasmaConcern && !isMildMelasma;
        const isHairConcern = HAIR_CONCERNS.some(hc => concern.toLowerCase().includes(hc));
        const isInTreatmentConcerns = treatmentConcernDisplayNames.some(
          (displayName: string): boolean => displayName.toLowerCase().trim() === concern.toLowerCase().trim()
        );
        return !isMainConcern && (concern !== melasmaConcern || isMelasmaButNotMild) && !isHairConcern && !isInTreatmentConcerns;
      })
      .map((concern: string): string => this.translateDetectedConcern(concern, userLanguage))
      .join(', ');
  }

  private translateDetectedConcern(concern: string, userLanguage: string): string {
    if (!concern) return concern;
    const normalized = concern.toLowerCase();
    const entry = concernsTranslation.find((c): boolean => c.en?.toLowerCase() === normalized);
    return entry?.[userLanguage] || entry?.en || concern;
  }

  private translateDetectedSeverity(severity: string, userLanguage: string): string {
    return concernSeverityTranslations[userLanguage]?.[severity]
      || concernSeverityTranslations[ApiClientConstant.LanguageString.EN]?.[severity]
      || severity;
  }

  private resolveHairOtherConcerns(detectedConcerns: string[], userLanguage: string = ApiClientConstant.LanguageString.EN): string {
    // For hair reports the secondary concern is shown only when temple baldness is detected.
    const hasTempleBaldness = detectedConcerns?.some(
      (concern: string): boolean => concern.toLowerCase().includes('temple-baldness')
    );
    return hasTempleBaldness ? this.translateDetectedConcern('Temple baldness', userLanguage) : '';
  }

  resolveSeverity(
    regimen: Regimen, firstFrontFaceCheckup: InstantCheckup | undefined, mainConcern: string,
    userLanguage: string = ApiClientConstant.LanguageString.EN,
  ): string {
    const severityFromRegimen = regimen?.displayConcern?.severity;
    if (severityFromRegimen) return this.translateDetectedSeverity(this.capitalizeFirstLetter(severityFromRegimen), userLanguage);

    const concernString = this.getConcernName(mainConcern);
    const checkupResults: DetectionResult[] = firstFrontFaceCheckup?.aiResponse?.result || [];
    const mainConcernDetections = checkupResults.find((r: DetectionResult): boolean => r?.ProblemName === concernString);

    const isDetected = mainConcernDetections?.Condition === this.appConfig.Shared.InstantCheckup.DetectionStatus.DETECTED;
    if (isDetected) {
      const raw = this.instantCheckupService.getSeverityFromConcern(mainConcernDetections);
      const detectedSeverity = (typeof raw === 'string' && raw)
        ? raw
        : this.appConfig.Shared.SkinReport.Severity.MODERATE;
      return this.translateDetectedSeverity(detectedSeverity, userLanguage);
    }
    return this.translateDetectedSeverity(this.appConfig.Shared.SkinReport.Severity.MODERATE, userLanguage);
  }

  getFirstFrontFaceCheckup(checkups: InstantCheckup[], isHair: boolean): InstantCheckup | undefined {
    if (!checkups?.length) return undefined;
    const targetType = isHair
      ? this.appConfig.Shared.InstantCheckup.Type.HAIR_FRONT
      : this.appConfig.Shared.InstantCheckup.Type.FRONT_FACE;
    return checkups.find((c: InstantCheckup): boolean => c.type === targetType);
  }

  async getOrCreateReportInfo(user: User): Promise<{ reportCode: string, patientID: string, createdAt: Date }> {
    const existingReport = user?.get('report') as { reportCode: string, patientID: string, createdAt: Date } | undefined;
    if (existingReport) {
      return existingReport;
    }

    const reportCode = `CS${this.generateRandomNumberWithDigits(7)}`;
    const patientID = `CS${this.generateRandomNumberWithDigits(4)}`;
    const createdAt = new Date();

    user?.set('report', { reportCode, patientID, createdAt });
    await user.save();

    return { reportCode, patientID, createdAt };
  }

  resolveHeroIngredients(regimen: Regimen, isRebranded: boolean, count: number, isHairReport: boolean = false): HeroIngredientsResult {
    if (!regimen?.variants) return { ingredients: [], show: false };

    const variantWithFiveProducts = regimen.variants.find((v: RegimenVariant): boolean => v?.variantId === 'variant_1999');
    const regimenProducts = extractRegimenProducts(variantWithFiveProducts);
    let ingredients: HeroIngredient[] = extractHeroIngredients(regimenProducts) || [];

    let show: boolean;
    if (isHairReport) {
      ingredients = ingredients.slice(0, ReportService.MAXIMUM_REQUIRED_HERO_INGREDIENTS);
      show = ingredients.length >= ReportService.MINIMUM_REQUIRED_HERO_INGREDIENTS;
    } else if (isRebranded && ingredients.length > 0) {
      ingredients = ingredients.slice(0, count);
      show = ingredients.length >= ReportService.MINIMUM_REQUIRED_HERO_INGREDIENTS;
    } else {
      ingredients = ingredients.slice(0, ReportService.MAXIMUM_REQUIRED_HERO_INGREDIENTS);
      show = ingredients.length === ReportService.MINIMUM_REQUIRED_HERO_INGREDIENTS
        || ingredients.length > ReportService.MINIMUM_REQUIRED_HERO_INGREDIENTS_EXISTING_UI;
    }

    return { ingredients, show };
  }

  shouldShowFirstJourney(mainConcern: string): boolean {
    return FIRST_JOURNEY_CONCERNS.includes(mainConcern);
  }

  getConcernName(concern: string): string {
    if (concern === CONCERN_NAMES.infAcne) {
      return this.appConfig.Shared.SkinReport.Concern.INFLAMMATORY_ACNE;
    }
    return concern;
  }

  capitalizeFirstLetter(str: string): string {
    if (!str) return '';
    return str.split(' ').map((item: string): string => item[0] + item.slice(1).toLowerCase()).join(' ');
  }

  private getMelasmaSeverity(firstFrontFaceCheckup: InstantCheckup | undefined): string {
    const results: DetectionResult[] = firstFrontFaceCheckup?.aiResponse?.result || [];
    const melasmaDetails = results.find(
      (d: DetectionResult): boolean => d.ProblemName === this.appConfig.Shared.SkinReport.Concern.MELASMA
        && d.Condition === this.appConfig.Shared.InstantCheckup.DetectionStatus.DETECTED);
    return melasmaDetails?.Severity;
  }

  private generateRandomNumberWithDigits(digits: number): string {
    const multiplier = 10 ** (digits - 1);
    return (Math.floor(Math.random() * 9 * multiplier) + multiplier).toString();
  }

  logSkinReportParams(concernsWithCount: ConcernsWithCount): void {
    this.eventLogger.trackInElasticSearch({
      event: 'SKIN_REPORT_PARAM_EMPTY',
      type: 'ERROR_SKIN_REPORT_PARAM_EMPTY',
      data: JSON.stringify(concernsWithCount),
    });
  }
}
